import { apiClient } from '@/core/api/client';
import type {
  ChannelsResponse,
  ChannelResponse,
  ChannelTasksResponse,
  ChannelParticipantsResponse,
  SubscriptionResponse,
  NotificationSettings,
  ChronologicCommunicationsResponse,
  CreateTrackerComRequest,
  TrackerComResponse,
  CreateTrackerComMetadataRequest,
  UpdateTrackerComMetadataRequest,
  TrackerComMetadataResponse,
  MessageTemplatesResponse,
  MessageTemplateResponse,
  CreateMessageTemplateRequest,
  UpdateMessageTemplateRequest
} from '../types/tracker.types';

export const trackerAPI = {
  // ============================================
  // CHANNEL MANAGEMENT
  // ============================================

  /**
   * Get all channels with subscription status
   * @param subscribedOnly - If true, only return subscribed channels
   * @param limit - Maximum number of channels to return (default: 200, max: 500)
   * @param offset - Number of channels to skip (default: 0)
   */
  async getAllChannels(
    subscribedOnly = false,
    limit = 200,
    offset = 0
  ): Promise<ChannelsResponse> {
    return apiClient.get<ChannelsResponse>('/api/tracker/channels', {
      params: {
        subscribed_only: subscribedOnly,
        limit,
        offset
      }
    });
  },

  /**
   * Get only channels the user is subscribed to
   */
  async getSubscribedChannels(): Promise<ChannelsResponse> {
    return apiClient.get<ChannelsResponse>('/api/tracker/channels/subscribed');
  },

  /**
   * Get channel by affaire ID (creates if doesn't exist)
   * @param affaireId - The affaire ID
   */
  async getChannelByAffaire(affaireId: number): Promise<ChannelResponse> {
    return apiClient.get<ChannelResponse>(`/api/tracker/channels/${affaireId}`);
  },

  /**
   * Search through all affaires in the database
   * @param searchQuery - The search query string
   */
  async searchChannels(searchQuery: string): Promise<ChannelsResponse> {
    return apiClient.get<ChannelsResponse>('/api/tracker/channels/search', {
      params: { q: searchQuery }
    });
  },

  // ============================================
  // SUBSCRIPTION MANAGEMENT
  // ============================================

  /**
   * Subscribe to a channel
   * @param channelId - The channel ID
   */
  async subscribeToChannel(channelId: string): Promise<SubscriptionResponse> {
    return apiClient.post<SubscriptionResponse>(`/api/tracker/channels/${channelId}/subscribe`);
  },

  /**
   * Unsubscribe from a channel
   * @param channelId - The channel ID
   * @param force - If true, bypass auto-subscription warning (default: false)
   */
  async unsubscribeFromChannel(channelId: string, force = false): Promise<SubscriptionResponse> {
    return apiClient.post<SubscriptionResponse>(`/api/tracker/channels/${channelId}/unsubscribe`, { force });
  },

  /**
   * Update notification settings for a channel
   * @param channelId - The channel ID
   * @param settings - Notification settings
   */
  async updateNotificationSettings(
    channelId: string,
    settings: {
      notifications_enabled?: boolean;
      notification_settings?: NotificationSettings;
    }
  ): Promise<SubscriptionResponse> {
    return apiClient.put<SubscriptionResponse>(`/api/tracker/channels/${channelId}/notifications`, settings);
  },

  // ============================================
  // TASKS & PARTICIPANTS
  // ============================================

  /**
   * Get all tasks for a channel
   * @param channelId - The channel ID
   */
  async getChannelTasks(channelId: string): Promise<ChannelTasksResponse> {
    return apiClient.get<ChannelTasksResponse>(`/api/tracker/channels/${channelId}/tasks`);
  },

  /**
   * Get all participants for a channel
   * @param channelId - The channel ID
   */
  async getChannelParticipants(channelId: string): Promise<ChannelParticipantsResponse> {
    return apiClient.get<ChannelParticipantsResponse>(`/api/tracker/channels/${channelId}/participants`);
  },

  // ============================================
  // CHRONOLOGIC COMMUNICATIONS
  // ============================================

  /**
   * Get all communications and status updates for an affaire in chronological order
   * @param affaireId - The affaire ID
   */
  async getChronologicCommunications(affaireId: number): Promise<ChronologicCommunicationsResponse> {
    return apiClient.get<ChronologicCommunicationsResponse>(`/api/tracker/chronologic/${affaireId}`);
  },

  // ============================================
  // UNREAD COUNT
  // ============================================

  /**
   * Get total unread count across all subscribed channels
   */
  async getUnreadCount(): Promise<{ success: boolean; data: { unread_count: number } }> {
    return apiClient.get<{ success: boolean; data: { unread_count: number } }>('/api/tracker/unread/count');
  },

  /**
   * Get unread count for a specific channel
   * @param channelId - The channel ID
   */
  async getChannelUnreadCount(channelId: string): Promise<{ success: boolean; data: { channel_id: string; unread_count: number } }> {
    return apiClient.get<{ success: boolean; data: { channel_id: string; unread_count: number } }>(`/api/tracker/channels/${channelId}/unread/count`);
  },

  /**
   * Get list of affaire_ids with unread content
   * Used to show notification badges per filter tab
   */
  async getUnreadAffaires(): Promise<{ success: boolean; data: { affaire_ids: number[] } }> {
    return apiClient.get<{ success: boolean; data: { affaire_ids: number[] } }>('/api/tracker/unread/affaires');
  },

  /**
   * Mark all tasks for an affaire as read
   * @param affaireId - The affaire ID
   */
  async markAffaireAllRead(affaireId: number): Promise<{ success: boolean; data: { marked_count: number }; message: string }> {
    return apiClient.post<{ success: boolean; data: { marked_count: number }; message: string }>(
      `/api/tracker/affaires/${affaireId}/mark-all-read`
    );
  },

  /**
   * Mark communications as opened when the assigned user first views the affaire detail
   * This is automatic tracking, separate from manual "mark as read"
   * Only sets opened_at for tracker_com and com_internes assigned to the current user
   * @param affaireId - The affaire ID
   */
  async markAffaireOpened(affaireId: number): Promise<{ success: boolean; data: { marked_count: number; tracker_coms: number; com_internes: number }; message: string }> {
    return apiClient.post<{ success: boolean; data: { marked_count: number; tracker_coms: number; com_internes: number }; message: string }>(
      `/api/tracker/affaires/${affaireId}/mark-opened`
    );
  },

  /**
   * Mark all tasks for an affaire as unread
   * @param affaireId - The affaire ID
   */
  async markAffaireAllUnread(affaireId: number): Promise<{ success: boolean; data: { marked_count: number }; message: string }> {
    return apiClient.post<{ success: boolean; data: { marked_count: number }; message: string }>(
      `/api/tracker/affaires/${affaireId}/mark-all-unread`
    );
  },

  /**
   * Bulk mark multiple affaires as read
   * @param affaireIds - Array of affaire IDs
   */
  async bulkMarkAffairesRead(affaireIds: number[]): Promise<{ success: boolean; data: { total_marked: number; affaires_processed: number }; message: string }> {
    return apiClient.post<{ success: boolean; data: { total_marked: number; affaires_processed: number }; message: string }>(
      '/api/tracker/affaires/bulk-mark-read',
      { affaire_ids: affaireIds }
    );
  },

  /**
   * Bulk mark multiple affaires as unread
   * @param affaireIds - Array of affaire IDs
   */
  async bulkMarkAffairesUnread(affaireIds: number[]): Promise<{ success: boolean; data: { total_marked: number; affaires_processed: number }; message: string }> {
    return apiClient.post<{ success: boolean; data: { total_marked: number; affaires_processed: number }; message: string }>(
      '/api/tracker/affaires/bulk-mark-unread',
      { affaire_ids: affaireIds }
    );
  },

  // ============================================
  // AFFAIRE TODOS
  // ============================================

  /**
   * Create or update a todo for an affaire
   * @param affaireId - The affaire ID
   * @param data - Todo data (all fields optional: tagged_user_id, priority, comment)
   */
  async createAffaireTodo(affaireId: number, data: { tagged_user_id?: string; priority?: 'none' | 'low' | 'medium' | 'high' | 'urgent'; comment?: string }) {
    return apiClient.post(`/api/tracker/affaires/${affaireId}/todo`, data);
  },

  /**
   * Get todo for a specific affaire
   * @param affaireId - The affaire ID
   */
  async getAffaireTodo(affaireId: number) {
    return apiClient.get(`/api/tracker/affaires/${affaireId}/todo`);
  },

  /**
   * Mark a todo as done
   * @param affaireId - The affaire ID
   */
  async markAffaireTodoDone(affaireId: number) {
    return apiClient.patch(`/api/tracker/affaires/${affaireId}/todo/done`);
  },

  /**
   * Delete a todo
   * @param affaireId - The affaire ID
   */
  async deleteAffaireTodo(affaireId: number) {
    return apiClient.delete(`/api/tracker/affaires/${affaireId}/todo`);
  },

  /**
   * Get all affaires where current user is targeted (for "A faire" filter)
   * Includes: todos, assigned communications, and com_internes where user is recipient
   * @param showClosed - If true, show completed items. Default: false (show open only)
   */
  async getMyTodos(showClosed = false) {
    return apiClient.get('/api/tracker/my-todos', {
      params: { show_closed: showClosed }
    });
  },

  /**
   * Get list of users eligible to be responsable (bureau/admin only)
   */
  async getEligibleResponsables(): Promise<{ success: boolean; data: Array<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
    fm_name: string;
  }> }> {
    return apiClient.get<{ success: boolean; data: Array<{
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      role: string;
      fm_name: string;
    }> }>('/api/tracker/affaires/eligible-responsables');
  },

  // ============================================
  // TRACKER COM (COMMUNICATION MESSAGES)
  // ============================================

  /**
   * Create a new tracker communication message
   * @param data - Message data (affaire_id and content required)
   */
  async createTrackerCom(data: CreateTrackerComRequest): Promise<TrackerComResponse> {
    return apiClient.post<TrackerComResponse>('/api/tracker/communications/tracker_com', data);
  },

  /**
   * Create metadata for a tracker communication message
   * @param trackerComId - The tracker_com ID
   * @param metadata - Metadata (urgency, assignment, notes, etc.)
   */
  async createTrackerComMetadata(
    trackerComId: string,
    metadata: CreateTrackerComMetadataRequest
  ): Promise<TrackerComMetadataResponse> {
    return apiClient.post<TrackerComMetadataResponse>(
      `/api/tracker/communications/tracker_com/${trackerComId}/metadata`,
      metadata
    );
  },

  /**
   * Update metadata for a tracker communication message
   * @param trackerComId - The tracker_com ID
   * @param metadata - Metadata updates (urgency, assignment, notes, etc.)
   */
  async updateTrackerComMetadata(
    trackerComId: string,
    metadata: UpdateTrackerComMetadataRequest
  ): Promise<TrackerComMetadataResponse> {
    return apiClient.put<TrackerComMetadataResponse>(
      `/api/tracker/communications/tracker_com/${trackerComId}/metadata`,
      metadata
    );
  },

  /**
   * Update a tracker communication message (author only)
   * @param trackerComId - The tracker_com ID
   * @param content - Updated message content
   */
  async updateTrackerCom(trackerComId: string, content: string): Promise<TrackerComResponse> {
    return apiClient.put<TrackerComResponse>(
      `/api/tracker/communications/tracker_com/${trackerComId}`,
      { content }
    );
  },

  /**
   * Delete a tracker communication message (author only, soft delete)
   * @param trackerComId - The tracker_com ID
   */
  async deleteTrackerCom(trackerComId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(
      `/api/tracker/communications/tracker_com/${trackerComId}`
    );
  },

  /**
   * Mark a tracker communication as done (for assigned users)
   * @param trackerComId - The tracker_com ID
   * @param isDone - Done status (true/false)
   * @param doneComment - Optional comment when marking as done
   */
  async markTrackerComDone(trackerComId: string, isDone: boolean, doneComment?: string): Promise<TrackerComMetadataResponse> {
    return apiClient.patch<TrackerComMetadataResponse>(
      `/api/tracker/communications/tracker_com/${trackerComId}/metadata/done`,
      { is_done: isDone, done_comment: doneComment }
    );
  },

  // ============================================
  // SMS (TRACKER-INTEGRATED SMS)
  // ============================================

  /**
   * Send SMS from tracker with metadata support
   * @param data - SMS data including affaire_id, phone_numbers, message, and optional metadata
   */
  async sendTrackerSMS(data: {
    affaire_id: number;
    phone_numbers: string[];
    message: string;
    urgency?: 'low' | 'medium' | 'high' | 'urgent';
    assigned_to?: string | null;
    comment?: string | null;
  }): Promise<{
    success: boolean;
    data: {
      sms_ids: string[];
      metadata_ids: string[];
      count: number;
    };
    message: string;
  }> {
    return apiClient.post('/api/tracker/communications/sms', data);
  },

  /**
   * Send email from tracker
   * @param data - Email data including affaire_id, to, cc, subject, body
   */
  async sendTrackerEmail(data: {
    affaire_id: number;
    to: string[];
    cc?: string[];
    subject: string;
    body: string;
  }): Promise<{
    success: boolean;
    data: any;
    message: string;
  }> {
    return apiClient.post('/api/admin/emails/send', {
      to: data.to,
      cc: data.cc,
      subject: data.subject,
      body: data.body,
      affaireId: data.affaire_id,
    });
  },

  /**
   * Reply to an email (uses Microsoft Graph threading)
   */
  async replyToEmail(emailId: string, data: {
    body: string;
    replyAll?: boolean;
    /** When provided, fully overrides the reply's To recipients. */
    to?: string[];
    /** When provided, sets the reply's CC recipients. */
    cc?: string[];
  }): Promise<{ success: boolean; data: any; message: string }> {
    return apiClient.post(`/api/admin/emails/${emailId}/reply`, data);
  },

  /**
   * Get contacts for an affaire (for SMS recipient selection)
   * @param affaireId - The affaire ID
   * @param opts.includeExpertUser - When true, append the affaire's assigned expert
   *   user account (always has an email) as an EXPERT contact if not already present.
   */
  async getAffaireContacts(affaireId: number, opts?: { includeExpertUser?: boolean }): Promise<{
    success: boolean;
    data: {
      affaire_id: number;
      total_contacts: number;
      contacts: Array<{
        k_id_contact: string;
        nom_representant: string;
        prenom: string;
        email_1?: string;
        tel_mobile?: string;
        roles: string[];
      }>;
    };
  }> {
    const qs = opts?.includeExpertUser ? '?includeExpertUser=true' : '';
    return apiClient.get(`/api/fm/affaire/${affaireId}/contacts${qs}`);
  },

  /**
   * Mark an SMS (outbox or inbox) as done
   * @param smsType - 'sms_outbox' or 'sms_inbox'
   * @param smsId - The SMS ID
   * @param isDone - Done status
   * @param doneComment - Optional comment
   */
  async markSMSDone(
    smsType: 'sms_outbox' | 'sms_inbox',
    smsId: string,
    isDone: boolean,
    doneComment?: string
  ): Promise<TrackerComMetadataResponse> {
    return apiClient.patch<TrackerComMetadataResponse>(
      `/api/tracker/communications/${smsType}/${smsId}/metadata/done`,
      { is_done: isDone, done_comment: doneComment }
    );
  },

  /**
   * Mark any communication type as done (generic version for all communication types)
   * @param type - Communication type (com_internes, tracker_com, com_avant_envoyer, etc.)
   * @param id - Communication ID
   * @param isDone - Done status (true/false)
   * @param doneComment - Optional comment when marking as done
   */
  async markCommunicationDone(
    type: string,
    id: string,
    isDone: boolean,
    doneComment?: string
  ): Promise<TrackerComMetadataResponse> {
    return apiClient.patch<TrackerComMetadataResponse>(
      `/api/tracker/communications/${type}/${id}/metadata/done`,
      { is_done: isDone, done_comment: doneComment }
    );
  },

  // ============================================
  // JUSTIFYING ITEMS (for "À faire" highlighting)
  // ============================================

  /**
   * Get items that justify why an affaire appears in "À faire - Ouvert" for the current user
   * Used to highlight communications in the chronologic view
   * Note: 'todo' source_type removed - "À faire" is now based on tracker_communication_metadata
   * @param affaireId - The affaire ID
   */
  async getJustifyingItems(affaireId: number): Promise<{
    success: boolean;
    data: Array<{
      source_type: 'communication' | 'status_based';
      communication_type: string | null;
      communication_id: string | null;
      priority: string;
      content: string | null;
      is_done: boolean;
      created_at: string;
    }>;
    count: number;
    affaire_id: number;
  }> {
    return apiClient.get(`/api/tracker/affaire/${affaireId}/justifying-items`);
  },

  // ============================================
  // MESSAGE TEMPLATES
  // ============================================

  /**
   * Get all message templates (system + user-created)
   */
  async getMessageTemplates(): Promise<MessageTemplatesResponse> {
    return apiClient.get<MessageTemplatesResponse>('/api/tracker/templates');
  },

  /**
   * Create a new message template
   * @param template - Template data
   */
  async createMessageTemplate(
    template: CreateMessageTemplateRequest
  ): Promise<MessageTemplateResponse> {
    return apiClient.post<MessageTemplateResponse>('/api/tracker/templates', template);
  },

  /**
   * Update an existing message template
   * @param id - Template ID
   * @param template - Updated template data
   */
  async updateMessageTemplate(
    id: string,
    template: UpdateMessageTemplateRequest
  ): Promise<MessageTemplateResponse> {
    return apiClient.put<MessageTemplateResponse>(`/api/tracker/templates/${id}`, template);
  },

  /**
   * Delete a message template
   * @param id - Template ID
   */
  async deleteMessageTemplate(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(`/api/tracker/templates/${id}`);
  }
};

export default trackerAPI;
